import { useEffect, useRef } from 'react';
import { useGooglePlaces } from '../../hooks/useGooglePlaces';
import useTripStore from '../../context/TripStore';

const LocationInput = ({ type = 'start', label }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const { isLoaded, isLoading, error, retry, initializeAutocomplete } = useGooglePlaces();
  const { trip, setTrip } = useTripStore();

  const isStart = type === 'start';
  const location = isStart ? trip.startLocation : trip.endLocation;
  const fieldName = isStart ? 'startLocation' : 'endLocation';

  // Initialize autocomplete when input is ready and Google Maps is loaded
  useEffect(() => {
    if (!isLoaded) {
      console.log(`LocationInput (${fieldName}): Waiting for Google Maps to load`);
      return;
    }

    if (!inputRef.current) {
      console.log(`LocationInput (${fieldName}): Waiting for input element`);
      return;
    }

    if (autocompleteRef.current) {
      console.log(`LocationInput (${fieldName}): Autocomplete already exists`);
      return;
    }

    const node = inputRef.current;
    
    // Longer delay on mobile to ensure everything is ready
    const delay = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 300 : 100;
    const timer = setTimeout(() => {
      if (!node) {
        console.warn(`LocationInput (${fieldName}): Input node disappeared`);
        return;
      }

      // Double-check Google Maps is available
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error(`LocationInput (${fieldName}): Google Maps Places not available`, {
          hasGoogle: !!window.google,
          hasMaps: !!window.google?.maps,
          hasPlaces: !!window.google?.maps?.places,
        });
        return;
      }

      console.log(`LocationInput (${fieldName}): Initializing autocomplete on input element`);
      
      // Ensure input is not disabled and is visible
      node.disabled = false;
      node.style.pointerEvents = 'auto';
      node.style.opacity = '1';
      node.style.position = 'relative';
      node.style.zIndex = '1';
      
      // Remove any existing autocomplete attribute that might interfere
      node.removeAttribute('autocomplete');
      
      try {
        const autocomplete = initializeAutocomplete(node, {
          types: ['establishment', 'geocode'],
        });

        if (autocomplete) {
          autocompleteRef.current = autocomplete;
          console.log(`LocationInput (${fieldName}): Autocomplete created successfully`);
          
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            console.log(`LocationInput (${fieldName}) place_changed event fired`);
            console.log('Full place object:', JSON.stringify(place, null, 2));
            
            // Check if place is valid
            if (!place) {
              console.warn('No place data received');
              return;
            }

            // Try to get location - handle both function and value formats
            let lat = null;
            let lng = null;
            
            if (place.geometry && place.geometry.location) {
              const loc = place.geometry.location;
              if (typeof loc.lat === 'function') {
                lat = loc.lat();
                lng = loc.lng();
              } else {
                lat = loc.lat;
                lng = loc.lng;
              }
            }

            // If we have a name and location, use it even if place_id is missing
            if (place.name && lat !== null && lng !== null) {
              console.log(`LocationInput (${fieldName}): Setting location data`, { 
                name: place.name, 
                placeId: place.place_id || 'unknown',
                lat, 
                lng 
              });
              
              setTrip({
                [fieldName]: {
                  name: place.name,
                  placeId: place.place_id || `temp_${Date.now()}`,
                  coordinates: {
                    lat,
                    lng,
                  },
                  address: place.formatted_address || '',
                },
              });
            } else {
              console.error(`LocationInput (${fieldName}): Place data incomplete`, {
                hasName: !!place.name,
                hasLat: lat !== null,
                hasLng: lng !== null,
                place: place
              });
            }
          });
        } else {
          console.error(`LocationInput (${fieldName}): initializeAutocomplete returned null`);
        }
      } catch (err) {
        console.error(`LocationInput (${fieldName}): Error creating autocomplete:`, err);
      }
    }, delay);

    return () => {
      clearTimeout(timer);
      // Clean up autocomplete on unmount
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isLoaded, initializeAutocomplete, setTrip, fieldName]);


  return (
    <div className="mb-5">
      <label htmlFor={fieldName} className="block text-xs font-light text-gray-600 mb-1.5 uppercase tracking-wide">
        {label || (isStart ? 'Starting Point' : 'End Point (optional)')}
      </label>
      <input
        ref={inputRef}
        id={fieldName}
        type="text"
        placeholder={isStart ? 'Hotel, airport, or address...' : 'Leave empty to return to start'}
        defaultValue={location?.name || ''}
        className="w-full px-3 py-2 text-sm font-light border border-gray-300 focus:outline-none focus:border-black"
        disabled={!isLoaded}
        autoComplete="off"
        style={{ position: 'relative', zIndex: 1 }}
      />
      {isLoading && !error && (
        <p className="mt-1 text-xs font-light text-gray-400">Loading Google Maps...</p>
      )}
      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-xs font-light text-red-800 mb-1">Sorry, something went wrong</p>
          <p className="text-xs font-light text-red-600 mb-2">{error}</p>
          <button
            onClick={retry}
            className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default LocationInput;

